import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Search, Building2, Filter } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminUsersGlobalPage() {
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTenant, setFilterTenant] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      let url = `${API}/super/all-users?`;
      if (search) url += `search=${encodeURIComponent(search)}&`;
      if (filterTenant !== 'all') url += `tenant_id=${filterTenant}&`;
      const [usersRes, tenantsRes] = await Promise.all([
        axios.get(url, { headers: getSuperAdminHeader() }),
        axios.get(`${API}/super/tenants`, { headers: getSuperAdminHeader() }),
      ]);
      setUsers(usersRes.data.users || []);
      setTenants(tenantsRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filterTenant]);

  useEffect(() => { const t = setTimeout(fetchData, 300); return () => clearTimeout(t); }, [fetchData]);

  const handleToggle = async (userId, isActive) => {
    try {
      await axios.put(`${API}/super/all-users/${userId}`, { is_active: !isActive }, { headers: getSuperAdminHeader() });
      toast.success(isActive ? 'Usuário desativado' : 'Usuário ativado');
      fetchData();
    } catch (err) { toast.error('Erro ao atualizar'); }
  };

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold">Gestão Global de Usuários</h2>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Buscar nome ou email..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={filterTenant} onValueChange={setFilterTenant}>
          <SelectTrigger className="w-56"><Filter className="h-3.5 w-3.5 mr-1.5" /><SelectValue placeholder="Filtrar por empresa" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as empresas</SelectItem>
            {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="text-sm">{users.length} usuários</Badge>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead className="w-20">Função</TableHead>
                <TableHead className="w-32">Telefone</TableHead>
                <TableHead className="w-20">Status</TableHead>
                <TableHead className="w-16">Ação</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-sm text-gray-500">{u.email}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs"><Building2 className="h-3 w-3 mr-1" />{u.company_name}</Badge></TableCell>
                  <TableCell><Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="text-xs">{u.role === 'admin' ? 'Admin' : 'Usuário'}</Badge></TableCell>
                  <TableCell className="text-xs text-gray-500 whitespace-nowrap">{u.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge className={u.is_active !== false ? 'bg-green-500 text-white text-xs' : 'bg-gray-200 text-gray-600 text-xs'}>
                      {u.is_active !== false ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Switch checked={u.is_active !== false} onCheckedChange={() => handleToggle(u.id, u.is_active !== false)} />
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">Nenhum usuário encontrado</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
